/**
 * Groups List Component
 * Displays and manages user's groups
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Groups as GroupsIcon,
  ExitToApp as LeaveIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { chatApi } from '@api/endpoints/chat'
import { GroupDto, GroupRole, getGroupRoleName } from '@api/types/social'
import { EditGroupDialog } from './EditGroupDialog'

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #43e97b, #38f9d7)',
  'linear-gradient(135deg, #fa709a, #fee140)',
  'linear-gradient(135deg, #a18cd1, #fbc2eb)',
  'linear-gradient(135deg, #fccb90, #d57eeb)',
]

function getAvatarGradient(name: string): string {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]
}

const GROUP_ICON_COLORS = [
  { bg: 'rgba(66,153,225,0.12)', color: '#4299e1' },
  { bg: 'rgba(56,178,172,0.12)', color: '#38b2ac' },
  { bg: 'rgba(212,168,83,0.12)', color: '#d4a853' },
  { bg: 'rgba(159,122,234,0.12)', color: '#9f7aea' },
  { bg: 'rgba(72,187,120,0.12)', color: '#48bb78' },
]

function getGroupIconColor(name: string) {
  let hash = 0
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return GROUP_ICON_COLORS[Math.abs(hash) % GROUP_ICON_COLORS.length]
}

interface GroupsListProps {
  filterText?: string
}

export function GroupsList({ filterText = '' }: GroupsListProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; group: GroupDto } | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<GroupDto | null>(null)

  // Fetch user's groups
  const { data: groupsData, isLoading, error } = useQuery({
    queryKey: ['my-groups'],
    queryFn: socialApi.getMyGroups,
  })

  // Apply text filter
  const groups = groupsData?.filter(g => {
    if (!filterText) return true

    const query = filterText.toLowerCase()
    return g.name.toLowerCase().includes(query) || g.description?.toLowerCase().includes(query)
  }) || []

  // Leave group mutation
  const leaveGroupMutation = useMutation({
    mutationFn: (groupId: string) => socialApi.leaveGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      setMenuAnchor(null)
    },
  })

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, group: GroupDto) => {
    event.stopPropagation()
    setMenuAnchor({ element: event.currentTarget, group })
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleViewGroup = () => {
    if (menuAnchor) {
      navigate(`/groups/${menuAnchor.group.id}`)
      handleMenuClose()
    }
  }

  const handleLeaveGroup = () => {
    const userRole = menuAnchor?.group.role ?? menuAnchor?.group.userRole
    if (menuAnchor && userRole !== GroupRole.Owner) {
      leaveGroupMutation.mutate(menuAnchor.group.id)
    }
  }

  const handleEditGroup = (e: React.MouseEvent, group: GroupDto) => {
    e.stopPropagation()
    setEditingGroup(group)
    setEditDialogOpen(true)
  }

  const handleEditDialogClose = () => {
    setEditDialogOpen(false)
    setEditingGroup(null)
  }

  const isOwner = (group: GroupDto) =>
    (group.role ?? group.userRole) === GroupRole.Owner || (group.role ?? group.userRole) === 3 || group.isOwner === true

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Failed to load groups'}
      </Alert>
    )
  }

  if (!groups || groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
        <GroupsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No Groups Yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Join or create a group to connect with other adventurers!
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {groups.map((group) => {
          const iconColor = getGroupIconColor(group.name)
          const userRole = group.role ?? group.userRole
          const roleName = getGroupRoleName(userRole ?? GroupRole.Member)
          const owner = isOwner(group)

          // Generate member stack placeholders
          const memberCount = group.memberCount || 0
          const stackCount = Math.min(memberCount, 3)
          const remaining = memberCount - stackCount

          return (
            <Box
              key={group.id}
              onClick={() => navigate(`/groups/${group.id}`)}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '14px',
                p: '14px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                transition: 'background 0.15s',
                '&:hover': {
                  filter: 'brightness(1.1)',
                },
              }}
            >
              {/* Top: Icon + Name + Description */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
                <Avatar
                  src={group.groupImage}
                  variant="rounded"
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: iconColor.bg,
                    color: iconColor.color,
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  <GroupsIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                    {group.name}
                  </Typography>
                  {group.description && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: 'text.secondary',
                        mt: 0.25,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {group.description}
                    </Typography>
                  )}
                </Box>

                {/* Quick actions */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  {owner && (
                    <IconButton size="small" onClick={(e) => handleEditGroup(e, group)} sx={{ color: 'primary.main' }}>
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                  {group.canAccessChat && (
                    <IconButton
                      size="small"
                      sx={{ color: 'primary.main' }}
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          const conversation = await chatApi.getOrJoinGroupChat(group.id)
                          navigate(`/chat?conversation=${conversation.id}`)
                        } catch (error) {
                          console.error('Failed to join group chat:', error)
                        }
                      }}
                    >
                      <ChatIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, group)}>
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Bottom: Member stack + count + role badge */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  pt: 1.25,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {/* Member stack */}
                <Box sx={{ display: 'flex', mr: 1 }}>
                  {Array.from({ length: stackCount }).map((_, i) => {
                    const initial = String.fromCharCode(65 + ((group.name.charCodeAt(i) || 65) % 26))
                    return (
                      <Avatar
                        key={i}
                        sx={{
                          width: 22,
                          height: 22,
                          fontSize: 9,
                          fontWeight: 700,
                          ml: i === 0 ? 0 : '-8px',
                          border: '2px solid',
                          borderColor: 'background.paper',
                          background: AVATAR_GRADIENTS[(i + group.name.charCodeAt(0)) % AVATAR_GRADIENTS.length],
                        }}
                      >
                        {initial}
                      </Avatar>
                    )
                  })}
                  {remaining > 0 && (
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: 9,
                        fontWeight: 600,
                        ml: '-8px',
                        border: '2px solid',
                        borderColor: 'background.paper',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: 'text.secondary',
                      }}
                    >
                      +{remaining}
                    </Avatar>
                  )}
                </Box>

                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{memberCount}</Box> member{memberCount !== 1 ? 's' : ''}
                </Typography>

                {/* Role badge */}
                <Box
                  sx={{
                    ml: 'auto',
                    px: 1,
                    py: 0.25,
                    borderRadius: '6px',
                    fontSize: 10,
                    fontWeight: 600,
                    ...(owner
                      ? { bgcolor: 'rgba(212,168,83,0.12)', color: '#d4a853' }
                      : { bgcolor: 'rgba(72,187,120,0.12)', color: '#48bb78' }),
                  }}
                >
                  {roleName}
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>

      {/* Group Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewGroup}>
          <ListItemIcon>
            <GroupsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Group</ListItemText>
        </MenuItem>
        {(menuAnchor?.group.role ?? menuAnchor?.group.userRole) !== GroupRole.Owner && (
          <MenuItem onClick={handleLeaveGroup} disabled={leaveGroupMutation.isPending}>
            <ListItemIcon>
              <LeaveIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Leave Group</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Edit Group Dialog */}
      {editingGroup && (
        <EditGroupDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          group={editingGroup}
        />
      )}
    </>
  )
}
